import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Grid, Box, Typography, List, ListItem, Divider, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, Paper } from '@mui/material';
import { styled } from '@mui/system';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LanguageIcon from '@mui/icons-material/Language';
import { ChevronLeft, ChevronRight, Print, Download, DragIndicator, Reorder } from '@mui/icons-material';

// --- Styled Components for Consistent Theming ---

const HeaderContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  paddingBottom: '20px',
  borderBottom: '1px solid #e0e0e0',
  marginBottom: '30px',
});

const NameTitle = styled(Typography)(({ color, font, fontSize, fontStyle, lineHeight }) => ({
  fontFamily: font || 'Montserrat, sans-serif',
  fontWeight: 800,
  lineHeight: lineHeight || 1.1,
  color: color || '#333',
  textTransform: 'uppercase',
  fontSize: fontSize || '2.5rem',
  fontStyle: fontStyle || 'normal',
}));

const ContactInfoItem = styled(Box)(({ color, font, fontSize, fontStyle, lineHeight }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '5px',
  fontFamily: font || 'Arial, sans-serif',
  color: color || '#555',
  fontSize: fontSize || '14px',
  fontStyle: fontStyle || 'normal',
  lineHeight: lineHeight || '20px',
  '& .MuiSvgIcon-root': {
    fontSize: '0.9rem',
    marginRight: '8px',
    color: 'inherit',
  },
}));

const MainSectionHeader = styled(Typography)(({ headerColor, font, fontSize, fontStyle, lineHeight }) => ({
  fontFamily: font || 'Montserrat, sans-serif',
  fontWeight: 700,
  fontSize: fontSize || '1.1rem',
  color: headerColor || '#333',
  textTransform: 'uppercase',
  position: 'relative',
  marginBottom: '15px',
  paddingLeft: '20px',
  fontStyle: fontStyle || 'normal',
  lineHeight: lineHeight || 'normal',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: '10px',
    height: '10px',
    backgroundColor: headerColor || '#333',
    borderRadius: '2px',
  },
}));

const SidebarSectionHeader = styled(Typography)(({ headerColor, font, fontSize, fontStyle, lineHeight }) => ({
  fontFamily: font || 'Montserrat, sans-serif',
  fontWeight: 700,
  fontSize: fontSize || '1rem',
  color: headerColor || '#fff',
  textTransform: 'uppercase',
  marginBottom: '15px',
  fontStyle: fontStyle || 'normal',
  lineHeight: lineHeight || 'normal',
}));

// Page size mapping
const pageSizeMap = {
  A4: { width: "210mm", height: "297mm" },
  Letter: { width: "216mm", height: "279mm" },
  Legal: { width: "216mm", height: "356mm" },
  A3: { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

// Font size mapping
const fontSizeMap = {
  small: "12px",
  normal: "14px",
  medium: "16px",
  large: "18px"
};

// Font style mapping
const fontStyleMap = {
  normal: "normal",
  italic: "italic"
};

// Default data
const defaultData = {
  firstName: "RICHARD",
  lastName: "MORRISON",
  role: "GRAPHIC DESIGNER, ART DIRECTOR",
  phone: "+0 123 456 789 0",
  email: "richard@company.com",
  city: "STREET LOCATION, CITY, COUNTRY NAME",
  profile: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Curabitur sit amet quam ut tortor pellentesque semper at dapibus metus. Pellentesque finibus bibendum tellus ac imperdiet. Quisque semper pulvinar est, ac pretium justo pharetra a.",
};

// Separate section orders for left and right sides
const defaultLeftSectionOrder = [
  'contact',
  'education',
  'skills'
];

const defaultRightSectionOrder = [
  'profile',
  'workExperience',
  'languages',
  'accomplishments',
  'certifications',
  'softwareSkills',
  'volunteering',
  'interests',
  'websites',
  'references',
  'additionalInfo'
];

// --- Resume Component ---

const Resume20 = ({
  color = '#333',
  nameColor = '#333',
  sidebarBackground = '#333',
  headerBackground = '#fff',
  sidebarTextColor = '#fff',
  font = 'Arial, sans-serif',
  fontSize = '14px',
  fontStyle = 'normal',
  headingSize = '24px',
  sectionSpacing = 50,
  paragraphSpacing = 30,
  lineSpacing = 20,
  topBottomMargin = 40,
  sideMargins = 40,
  paragraphIndent = 0,
  lineWeight = 1,
  pageSize = 'A4',
  theme,
  formData = {},
  workExperiences = [],
  enableDragDrop = true,
  exporting = false,
  linkedIn = '',
  website = '',
}) => {
  // Get effective font size
  const getBaseFontSize = () => {
    if (typeof fontSize === 'string') {
      const effective = fontSizeMap[fontSize] || fontSize || "14px";
      return parseInt(effective, 10);
    }
    return 14;
  };

  // Get font size with multiplier
  const getFontSizeWithMultiplier = (multiplier = 1) => {
    const baseSize = getBaseFontSize();
    return `${baseSize * multiplier}px`;
  };

  // Get line height
  const getLineHeight = () => `${lineSpacing}px`;

  // Get heading size
  const getHeadingSize = () => {
    if (typeof headingSize === 'string') {
      return headingSize;
    }
    return `${getBaseFontSize() * 1.7}px`;
  };

  // Get subheading size
  const getSubheadingSize = () => `${getBaseFontSize() * 1.2}px`;

  // Get font style
  const getFontStyle = () => {
    if (typeof fontStyle === 'string') {
      return fontStyleMap[fontStyle] || fontStyle;
    }
    return 'normal';
  };

  // Common text styles
  const textStyles = {
    fontFamily: font,
    fontSize: getFontSizeWithMultiplier(),
    fontStyle: getFontStyle(),
    lineHeight: getLineHeight(),
    textIndent: `${paragraphIndent}px`,
  };

  // Common heading styles
  const headingStyles = {
    ...textStyles,
    fontSize: getHeadingSize(),
    fontWeight: "bold",
  };

  // Common subheading styles
  const subheadingStyles = {
    ...textStyles,
    fontSize: getSubheadingSize(),
    fontWeight: "bold",
  };

  // State for pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState([]);
  const mainContentRef = useRef(null);
  const pageContentRef = useRef(null);
  const [isMeasuring, setIsMeasuring] = useState(false);

  // Data states
  const [educationEntries, setEducationEntries] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [savedSummary, setSavedSummary] = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});

  // State for section ordering
  const [leftSectionOrder, setLeftSectionOrder] = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // Merge default data with form data
  const data = {
    ...defaultData,
    ...formData,
    linkedIn: linkedIn || formData.linkedIn || '',
    website: website || formData.website || formData.portfolio || '',
  };
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;

  const primaryColor = theme?.primary || color;

  const palette = {
    mainBackground: '#FFFFFF',
    sidebarBackground: sidebarBackground,
    accentColor: primaryColor,
    textColor: '#333333',
    lightText: '#666666',
    header: headerBackground,
    nameColor: nameColor,
    sidebarText: sidebarTextColor,
  };

  // Convert mm to pixels (1mm = 3.779527559px)
  const mmToPx = (mm) => mm * 3.779527559;
  
  // Get page dimensions in pixels
  const pageWidthPx = mmToPx(parseFloat(page.width));
  const pageHeightPx = mmToPx(parseFloat(page.height));
  const mainColumnWidthPx = (pageWidthPx * 0.7) - (2 * sideMargins);
  const sidebarColumnWidthPx = (pageWidthPx * 0.3) - (2 * sideMargins);

  // Load data from localStorage
  useEffect(() => {
    const storedEntries = localStorage.getItem("educationEntries");
    if (storedEntries) {
      setEducationEntries(JSON.parse(storedEntries));
    }
    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) {
      setSavedSkills(JSON.parse(storedSkills));
    }
    const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
    if (storedSummaries.length > 0) {
      setSavedSummary(storedSummaries[storedSummaries.length - 1]);
    }
    const storedAdditionalInfo = JSON.parse(localStorage.getItem("additionalInfo")) || [];
    setSavedAdditionalInfo(storedAdditionalInfo);
    const storedLanguages = JSON.parse(localStorage.getItem("languagesList")) || [];
    setSavedLanguages(storedLanguages);
    const storedAccomplishments = JSON.parse(localStorage.getItem("accomplishmentsList")) || [];
    setSavedAccomplishments(storedAccomplishments);
    const storedCertifications = JSON.parse(localStorage.getItem("certificationsList")) || [];
    setSavedCertifications(storedCertifications);
    const storedReferences = JSON.parse(localStorage.getItem("referencesList")) || [];
    setSavedReferences(storedReferences);
    const storedSoftwareSkills = JSON.parse(localStorage.getItem("softwareSkills")) || [];
    setSoftwareSkills(storedSoftwareSkills);
    const storedVolunteering = JSON.parse(localStorage.getItem("volunteering")) || [];
    setSavedVolunteering(storedVolunteering);
    const storedInterests = JSON.parse(localStorage.getItem("interests")) || [];
    setSavedInterests(storedInterests);
    const storedWebsites = JSON.parse(localStorage.getItem("websites")) || [];
    setSavedWebsites(storedWebsites);

    const userId = localStorage.getItem("userId");
    let sectionsData = {};

    if (userId) {
      const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
      if (currentCustomSections.length > 0) {
        currentCustomSections.forEach(sectionName => {
          const userCustomKey = `custom_${userId}_${sectionName}`;
          try {
            const customSectionData = localStorage.getItem(userCustomKey);
            if (customSectionData) {
              const parsedData = JSON.parse(customSectionData);
              const items = parsedData.items || [];
              const validItems = items.filter(item => item && item.trim() !== '');
              if (validItems.length > 0) {
                sectionsData[sectionName] = validItems;
              }
            }
          } catch (error) {
            console.error(`Error loading custom section ${sectionName}:`, error);
          }
        });
      }
    }

    setCustomSections(sectionsData);

    const savedLeftSectionOrder = localStorage.getItem("resume20LeftSectionOrder");
    let initialLeftOrder = savedLeftSectionOrder 
      ? JSON.parse(savedLeftSectionOrder).filter(section => !section.startsWith('custom_'))
      : [...defaultLeftSectionOrder];

    const savedRightSectionOrder = localStorage.getItem("resume20RightSectionOrder");
    let initialRightOrder = savedRightSectionOrder
      ? JSON.parse(savedRightSectionOrder).filter(section => !section.startsWith('custom_'))
      : [...defaultRightSectionOrder];

    // Ensure 'websites' is in one of the section lists
    const hasWebsites = initialLeftOrder.includes('websites') || initialRightOrder.includes('websites');
    if (!hasWebsites) {
      initialRightOrder.push('websites');
    }

    setLeftSectionOrder(initialLeftOrder);

    const currentCustomSectionIds = Object.keys(sectionsData).map(name => `custom_${name}`);
    const allRightSections = [...initialRightOrder, ...currentCustomSectionIds];
    setRightSectionOrder(allRightSections);
  }, []);

  // Update custom sections in right order
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId && Object.keys(customSections).length > 0) {
      const currentRightOrder = [...rightSectionOrder];
      const currentCustomSectionIds = Object.keys(customSections).map(name => `custom_${name}`);
      const filteredRightOrder = currentRightOrder.filter(section => !section.startsWith('custom_'));
      const newRightOrder = [...filteredRightOrder, ...currentCustomSectionIds];
      if (JSON.stringify(newRightOrder) !== JSON.stringify(currentRightOrder)) {
        setRightSectionOrder(newRightOrder);
        localStorage.setItem("resume20RightSectionOrder", JSON.stringify(newRightOrder));
      }
      const currentLeftOrder = [...leftSectionOrder];
      const filteredLeftOrder = currentLeftOrder.filter(section => !section.startsWith('custom_'));
      if (JSON.stringify(filteredLeftOrder) !== JSON.stringify(currentLeftOrder)) {
        setLeftSectionOrder(filteredLeftOrder);
        localStorage.setItem("resume20LeftSectionOrder", JSON.stringify(filteredLeftOrder));
      }
    } else if (userId && Object.keys(customSections).length === 0) {
      const currentRightOrder = [...rightSectionOrder];
      const filteredRightOrder = currentRightOrder.filter(section => !section.startsWith('custom_'));
      if (filteredRightOrder.length !== currentRightOrder.length) {
        setRightSectionOrder(filteredRightOrder);
        localStorage.setItem("resume20RightSectionOrder", JSON.stringify(filteredRightOrder));
      }
    }
  }, [customSections]);

  useEffect(() => {
    if (leftSectionOrder.length > 0) {
      localStorage.setItem("resume20LeftSectionOrder", JSON.stringify(leftSectionOrder));
    }
  }, [leftSectionOrder]);

  useEffect(() => {
    if (rightSectionOrder.length > 0) {
      localStorage.setItem("resume20RightSectionOrder", JSON.stringify(rightSectionOrder));
    }
  }, [rightSectionOrder]);

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  // Drag and drop handlers
  const handleLeftDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({
      index,
      side: 'left',
      sectionId: leftSectionOrder[index]
    }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleRightDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({
      index,
      side: 'right',
      sectionId: rightSectionOrder[index]
    }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragOver = (e, index, side) => {
    e.preventDefault();
    setDragOverIndex(index);
    setDragOverSide(side);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  const handleLeftDrop = (e, targetIndex) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    const sourceIndex = data.index;
    const sourceSide = data.side;
    const sectionId = data.sectionId;

    if (sourceSide === 'left' && sourceIndex !== targetIndex) {
      const newSectionOrder = [...leftSectionOrder];
      const [movedSection] = newSectionOrder.splice(sourceIndex, 1);
      newSectionOrder.splice(targetIndex, 0, movedSection);
      setLeftSectionOrder(newSectionOrder);
    } else if (sourceSide === 'right') {
      const newLeftSectionOrder = [...leftSectionOrder];
      const newRightSectionOrder = [...rightSectionOrder];
      newRightSectionOrder.splice(sourceIndex, 1);
      newLeftSectionOrder.splice(targetIndex, 0, sectionId);
      setLeftSectionOrder(newLeftSectionOrder);
      setRightSectionOrder(newRightSectionOrder);
    }
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleRightDrop = (e, targetIndex) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    const sourceIndex = data.index;
    const sourceSide = data.side;
    const sectionId = data.sectionId;

    if (sourceSide === 'right' && sourceIndex !== targetIndex) {
      const newSectionOrder = [...rightSectionOrder];
      const [movedSection] = newSectionOrder.splice(sourceIndex, 1);
      newSectionOrder.splice(targetIndex, 0, movedSection);
      setRightSectionOrder(newSectionOrder);
    } else if (sourceSide === 'left') {
      const newLeftSectionOrder = [...leftSectionOrder];
      const newRightSectionOrder = [...rightSectionOrder];
      newLeftSectionOrder.splice(sourceIndex, 1);
      newRightSectionOrder.splice(targetIndex, 0, sectionId);
      setLeftSectionOrder(newLeftSectionOrder);
      setRightSectionOrder(newRightSectionOrder);
    }
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleDragEnd = (e) => {
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const resetLeftSectionOrder = () => setLeftSectionOrder(defaultLeftSectionOrder);
  const resetRightSectionOrder = () => setRightSectionOrder(defaultRightSectionOrder);
  const resetAllSectionOrder = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
    setRightSectionOrder(defaultRightSectionOrder);
  };

  const handleArrangeSectionsClick = () => setArrangeDialogOpen(true);
  const handleArrangeDialogClose = () => setArrangeDialogOpen(false);

  const getSectionTitle = (sectionId) => {
    const titles = {
      'contact': 'Contact',
      'education': 'Education',
      'skills': 'Skills',
      'profile': 'Profile',
      'workExperience': 'Work Experience',
      'languages': 'Languages',
      'accomplishments': 'Accomplishments',
      'certifications': 'Certifications',
      'softwareSkills': 'Software Skills',
      'volunteering': 'Volunteering',
      'interests': 'Interests',
      'websites': 'Websites & Profiles',
      'references': 'References',
      'additionalInfo': 'Additional Information'
    };
    if (sectionId.startsWith('custom_')) {
      return sectionId.replace('custom_', '').toUpperCase();
    }
    return titles[sectionId] || sectionId;
  };

  // Helper function to measure section height
  const measureSectionHeight = (sectionElement, width) => {
    const clone = sectionElement.cloneNode(true);
    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.width = `${width}px`;
    clone.style.fontFamily = font;
    clone.style.fontSize = fontSize;
    clone.style.fontStyle = fontStyle;
    clone.style.lineHeight = `${lineSpacing}px`;
    document.body.appendChild(clone);
    const height = clone.offsetHeight;
    document.body.removeChild(clone);
    return height;
  };

  // Get all section elements with their heights
  const getSectionElementsWithHeights = (container, selector, width) => {
    const sections = Array.from(container.querySelectorAll(selector));
    return sections.map(section => ({
      html: section.outerHTML,
      height: measureSectionHeight(section, width)
    }));
  };

  // Render header section HTML for measurement
  const getHeaderHTML = () => {
    return `
      <div class="header-section-measure" style="padding: ${topBottomMargin}px ${sideMargins}px ${topBottomMargin / 2}px ${sideMargins}px; font-family: ${font}; font-size: ${getFontSizeWithMultiplier()}; font-style: ${getFontStyle()}; line-height: ${getLineHeight()};">
        <div style="display: flex; align-items: center; justify-content: space-between;">
          <div style="width: 70%;">
            <div style="display: flex; align-items: center; padding-bottom: 20px; border-bottom: 1px solid #e0e0e0; margin-bottom: 30px;">
              <div style="padding: 12px; margin-right: 16px; background-color: ${palette.accentColor}; border-radius: 4px; display: flex; align-items: center; justify-content: center;">
                <svg width="${parseInt(getFontSizeWithMultiplier(2.5))}" height="${parseInt(getFontSizeWithMultiplier(2.5))}" viewBox="0 0 24 24" fill="white">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              </div>
              <div>
                <div style="display: flex;">
                  <div style="font-family: ${font}; font-weight: 800; line-height: 1.1; color: ${palette.nameColor}; font-size: ${getFontSizeWithMultiplier(2.5)}; font-style: ${getFontStyle()}; text-transform: uppercase;">${data.firstName}</div>
                  <div style="font-family: ${font}; font-weight: 800; line-height: 1.1; color: ${palette.nameColor}; font-size: ${getFontSizeWithMultiplier(2.5)}; font-style: ${getFontStyle()}; text-transform: uppercase; margin-left: 8px;">${data.lastName}</div>
                </div>
                <div style="color: ${palette.lightText}; margin-top: 4px; font-family: ${font}; font-weight: 500; letter-spacing: 1px; font-size: ${getFontSizeWithMultiplier(1.1)}; font-style: ${getFontStyle()}; line-height: ${getLineHeight()};">${data.currentPosition || data.role}</div>
              </div>
            </div>
          </div>
          <div style="width: 30%; text-align: right;">
            <div style="display: flex; align-items: center; margin-bottom: 5px; justify-content: flex-end;">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="${palette.accentColor}" style="margin-right: 8px;">
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
              </svg>
              <span style="color: ${palette.textColor}; font-family: ${font}; font-size: ${getFontSizeWithMultiplier()}; font-style: ${getFontStyle()}; line-height: ${getLineHeight()};">${data.email}</span>
            </div>
            <div style="display: flex; align-items: center; margin-bottom: 5px; justify-content: flex-end;">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="${palette.accentColor}" style="margin-right: 8px;">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
              </svg>
              <span style="color: ${palette.textColor}; font-family: ${font}; font-size: ${getFontSizeWithMultiplier()}; font-style: ${getFontStyle()}; line-height: ${getLineHeight()};">${data.phone}</span>
            </div>
            <div style="display: flex; align-items: center; margin-bottom: 5px; justify-content: flex-end;">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="${palette.accentColor}" style="margin-right: 8px;">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              <span style="color: ${palette.textColor}; font-family: ${font}; font-size: ${getFontSizeWithMultiplier()}; font-style: ${getFontStyle()}; line-height: ${getLineHeight()};">${data.city}</span>
            </div>
            ${data.linkedIn ? `
            <div style="display: flex; align-items: center; margin-bottom: 5px; justify-content: flex-end;">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="${palette.accentColor}" style="margin-right: 8px;">
                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
              </svg>
              <span style="color: ${palette.textColor}; font-family: ${font}; font-size: ${getFontSizeWithMultiplier()}; font-style: ${getFontStyle()}; line-height: ${getLineHeight()};">${data.linkedIn}</span>
            </div>
            ` : ''}
            ${data.website ? `
            <div style="display: flex; align-items: center; margin-bottom: 5px; justify-content: flex-end;">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="${palette.accentColor}" style="margin-right: 8px;">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
              <span style="color: ${palette.textColor}; font-family: ${font}; font-size: ${getFontSizeWithMultiplier()}; font-style: ${getFontStyle()}; line-height: ${getLineHeight()};">${data.website}</span>
            </div>
            ` : ''}
          </div>
        </div>
      </div>
    `;
  };

  // Render section content as HTML string for measurement
  const renderSectionHTML = (sectionId, inSidebar = false) => {
    const textColor = inSidebar ? palette.sidebarText : palette.textColor;
    const headerColor = inSidebar ? palette.sidebarText : palette.accentColor;
    const headerFontSize = getHeadingSize();
    
    const getHeaderHTML = (title) => {
      if (inSidebar) {
        return `<div style="font-family: ${font}; font-weight: 700; font-size: ${headerFontSize}; color: ${headerColor}; text-transform: uppercase; margin-bottom: 15px; font-style: ${getFontStyle()}; line-height: normal;">${title}</div>`;
      } else {
        return `<div style="font-family: ${font}; font-weight: 700; font-size: ${headerFontSize}; color: ${headerColor}; text-transform: uppercase; position: relative; margin-bottom: 15px; padding-left: 20px; font-style: ${getFontStyle()}; line-height: normal;">
                  <div style="position: absolute; left: 0; top: 50%; transform: translateY(-50%); width: 10px; height: 10px; background-color: ${headerColor}; border-radius: 2px;"></div>
                  ${title}
                </div>`;
      }
    };

    const getTextStyle = () => {
      return `font-family: ${font}; font-size: ${getFontSizeWithMultiplier()}; font-style: ${getFontStyle()}; line-height: ${getLineHeight()}; text-indent: ${paragraphIndent}px;`;
    };

    const getListStyle = () => {
      return `font-family: ${font}; font-size: ${getFontSizeWithMultiplier()}; font-style: ${getFontStyle()}; line-height: ${getLineHeight()}; text-indent: ${paragraphIndent}px; margin-bottom: ${paragraphSpacing / 20}px;`;
    };

    const getSubheadingStyle = () => {
      return `font-family: ${font}; font-size: ${getSubheadingSize()}; font-style: ${getFontStyle()}; line-height: ${getLineHeight()}; font-weight: bold;`;
    };

    switch (sectionId) {
      case 'profile':
        return `
          <div class="resume-section" style="margin-bottom: ${sectionSpacing / 10}px;">
            ${getHeaderHTML('PROFILE')}
            <div style="color: ${textColor}; ${getTextStyle()}">${savedSummary || data.profile}</div>
          </div>
        `;

      case 'workExperience':
        if (!workExperiences || workExperiences.length === 0) {
          return `
            <div class="resume-section" style="margin-bottom: ${sectionSpacing / 10}px;">
              ${getHeaderHTML('WORK EXPERIENCE')}
              <div style="margin-bottom: ${paragraphSpacing / 10}px;">
                <div style="${getSubheadingStyle()}; color: ${textColor};">GRAPHIC DESIGNER</div>
                <div style="font-style: ${getFontStyle()}; color: ${inSidebar ? 'rgba(255,255,255,0.8)' : palette.lightText}; ${getTextStyle()}">Lorem Ipsum Digital Agency (2017-2019)</div>
                <div style="color: ${textColor}; margin-top: ${paragraphSpacing / 10}px; ${getTextStyle()}">Curabitur sit amet quam ut tortor pellentesque semper at dapibus metus.</div>
              </div>
            </div>
          `;
        }
        return `
          <div class="resume-section" style="margin-bottom: ${sectionSpacing / 10}px;">
            ${getHeaderHTML('WORK EXPERIENCE')}
            ${workExperiences.map(work => `
              <div style="margin-bottom: ${paragraphSpacing / 10}px;">
                <div style="${getSubheadingStyle()}; color: ${textColor};">${work.jobTitle}</div>
                <div style="font-style: ${getFontStyle()}; color: ${inSidebar ? 'rgba(255,255,255,0.8)' : palette.lightText}; ${getTextStyle()}">${work.employer} (${formatDate(work.startMonth, work.startYear)} - ${work.current ? "Present" : formatDate(work.endMonth, work.endYear)})</div>
                ${work.description ? `<div style="color: ${textColor}; margin-top: ${paragraphSpacing / 10}px; ${getTextStyle()}">${work.description}</div>` : ''}
              </div>
            `).join('')}
          </div>
        `;

      case 'education':
        if (educationEntries.length === 0) return '';
        return `
          <div class="resume-section" style="margin-bottom: ${sectionSpacing / 10}px;">
            ${getHeaderHTML('EDUCATION')}
            ${educationEntries.map((edu, index) => {
              if (edu.educationLevel === "school") {
                return `
                  <div style="margin-bottom: ${index === educationEntries.length - 1 ? 0 : paragraphSpacing / 10}px;">
                    <div style="${getSubheadingStyle()}; color: ${textColor};">${edu.standard} Standard ${edu.stream ? `- ${edu.stream}` : ''}</div>
                    <div style="font-style: ${getFontStyle()}; color: ${inSidebar ? 'rgba(255,255,255,0.8)' : palette.lightText}; ${getTextStyle()}">${edu.schoolName} ${edu.schoolLocation ? `(${edu.schoolLocation})` : ''}</div>
                    <div style="font-style: ${getFontStyle()}; color: ${inSidebar ? 'rgba(255,255,255,0.8)' : palette.lightText}; ${getTextStyle()}">${edu.percentage ? `Percentage: ${edu.percentage}%` : ''}</div>
                  </div>
                `;
              } else if (edu.educationLevel === "college") {
                return `
                  <div style="margin-bottom: ${index === educationEntries.length - 1 ? 0 : paragraphSpacing / 10}px;">
                    <div style="${getSubheadingStyle()}; color: ${textColor};">${edu.degreeName} ${edu.specialization ? `- ${edu.specialization}` : ''}</div>
                    <div style="font-style: ${getFontStyle()}; color: ${inSidebar ? 'rgba(255,255,255,0.8)' : palette.lightText}; ${getTextStyle()}">${edu.collegeName} ${edu.collegeLocation ? `(${edu.collegeLocation})` : ''} (${formatDate(edu.gradMonth, edu.gradYear)})</div>
                    <div style="font-style: ${getFontStyle()}; color: ${inSidebar ? 'rgba(255,255,255,0.8)' : palette.lightText}; ${getTextStyle()}">${edu.cgpa ? (edu.programType ? `CGPA: ${edu.cgpa}` : `CGPA: ${edu.cgpa}`) : ''}${edu.grade ? (edu.cgpa ? ` | Grade: ${edu.grade}` : `Grade: ${edu.grade}`) : ''}</div>
                    ${edu.additionalCoursework ? `<div style="font-style: ${getFontStyle()}; color: ${inSidebar ? 'rgba(255,255,255,0.8)' : palette.lightText}; ${getTextStyle()}">${edu.additionalCoursework}</div>` : ''}
                  </div>
                `;
              }
              return '';
            }).join('')}
          </div>
        `;

      case 'skills':
        return `
          <div class="resume-section" style="margin-bottom: ${sectionSpacing / 10}px;">
            ${getHeaderHTML('SKILLS')}
            <ul style="list-style-type: disc; padding-left: 20px; margin: 0;">
              ${(savedSkills.length > 0 ? savedSkills : [{name: 'Graphic Design'}, {name: 'Leadership'}, {name: 'Web Development'}]).map(skill => `
                <li style="color: ${textColor}; ${getListStyle()}">${skill.name} ${skill.rating ? `(${skill.rating}%)` : ''}</li>
              `).join('')}
            </ul>
          </div>
        `;

      case 'languages':
        if (savedLanguages.length === 0) return '';
        return `
          <div class="resume-section" style="margin-bottom: ${sectionSpacing / 10}px;">
            ${getHeaderHTML('LANGUAGES')}
            <ul style="list-style-type: disc; padding-left: 20px; margin: 0;">
              ${savedLanguages.map(lang => `
                <li style="color: ${textColor}; ${getListStyle()}">${lang.name}${lang.level ? ` - ${lang.level}` : ''}</li>
              `).join('')}
            </ul>
          </div>
        `;

      case 'accomplishments':
        if (savedAccomplishments.length === 0) return '';
        return `
          <div class="resume-section" style="margin-bottom: ${sectionSpacing / 10}px;">
            ${getHeaderHTML('ACCOMPLISHMENTS')}
            <ul style="list-style-type: disc; padding-left: 20px; margin: 0;">
              ${savedAccomplishments.map(acc => `
                <li style="color: ${textColor}; ${getListStyle()}">${acc}</li>
              `).join('')}
            </ul>
          </div>
        `;

      case 'certifications':
        if (savedCertifications.length === 0) return '';
        return `
          <div class="resume-section" style="margin-bottom: ${sectionSpacing / 10}px;">
            ${getHeaderHTML('CERTIFICATIONS')}
            <ul style="list-style-type: disc; padding-left: 20px; margin: 0;">
              ${savedCertifications.map(cert => `
                <li style="color: ${textColor}; ${getListStyle()}">${cert.name} - ${cert.provider} (${cert.year})</li>
              `).join('')}
            </ul>
          </div>
        `;

      case 'softwareSkills':
        if (softwareSkills.length === 0) return '';
        return `
          <div class="resume-section" style="margin-bottom: ${sectionSpacing / 10}px;">
            ${getHeaderHTML('SOFTWARE SKILLS')}
            <ul style="list-style-type: disc; padding-left: 20px; margin: 0;">
              ${softwareSkills.map(skill => `
                <li style="color: ${textColor}; ${getListStyle()}">${skill.name} - ${skill.level}%</li>
              `).join('')}
            </ul>
          </div>
        `;

      case 'volunteering':
        if (savedVolunteering.length === 0) return '';
        return `
          <div class="resume-section" style="margin-bottom: ${sectionSpacing / 10}px;">
            ${getHeaderHTML('VOLUNTEERING')}
            ${savedVolunteering.map(vol => `
              <div style="margin-bottom: ${paragraphSpacing / 10}px;">
                <div style="${getSubheadingStyle()}; color: ${textColor};">${vol.subtopic}</div>
                <div style="font-style: ${getFontStyle()}; color: ${inSidebar ? 'rgba(255,255,255,0.8)' : palette.lightText}; ${getTextStyle()}">${vol.fromDate} - ${vol.toDate}</div>
                <div style="color: ${textColor}; margin-top: ${paragraphSpacing / 20}px; ${getTextStyle()}">${vol.content}</div>
              </div>
            `).join('')}
          </div>
        `;

      case 'interests':
        if (savedInterests.length === 0) return '';
        return `
          <div class="resume-section" style="margin-bottom: ${sectionSpacing / 10}px;">
            ${getHeaderHTML('INTERESTS')}
            <ul style="list-style-type: disc; padding-left: 20px; margin: 0;">
              ${savedInterests.map(interest => `
                <li style="color: ${textColor}; ${getListStyle()}">${interest}</li>
              `).join('')}
            </ul>
          </div>
        `;

      case 'references':
        if (savedReferences.length === 0) return '';
        return `
          <div class="resume-section" style="margin-bottom: ${sectionSpacing / 10}px;">
            ${getHeaderHTML('REFERENCES')}
            <ul style="list-style-type: disc; padding-left: 20px; margin: 0;">
              ${savedReferences.map(ref => `
                <li style="color: ${textColor}; ${getListStyle()}">${ref}</li>
              `).join('')}
            </ul>
          </div>
        `;

      case 'additionalInfo':
        if (savedAdditionalInfo.length === 0) return '';
        return `
          <div class="resume-section" style="margin-bottom: ${sectionSpacing / 10}px;">
            ${getHeaderHTML('ADDITIONAL INFORMATION')}
            <ul style="list-style-type: disc; padding-left: 20px; margin: 0;">
              ${savedAdditionalInfo.map(info => `
                <li style="color: ${textColor}; ${getListStyle()}">${info}</li>
              `).join('')}
            </ul>
          </div>
        `;

      case 'websites':
        if (savedWebsites.length === 0) return '';
        return `
          <div class="resume-section" style="margin-bottom: ${sectionSpacing / 10}px;">
            ${getHeaderHTML('WEBSITES & PROFILES')}
            <ul style="list-style-type: disc; padding-left: 20px; margin: 0;">
              ${savedWebsites.map(item => {
                const label = typeof item === 'string' ? item : (item.label || item.name || item.title || '');
                const url = typeof item === 'object' ? (item.url || item.link || '') : '';
                const displayVal = url && label !== url ? `${label}: ${url}` : (label || url);
                return `<li style="color: ${textColor}; ${getListStyle()}">${displayVal}</li>`;
              }).join('')}
            </ul>
          </div>
        `;

      default:
        if (sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName] || [];
          if (items.length === 0) return '';
          return `
            <div class="resume-section" style="margin-bottom: ${sectionSpacing / 10}px;">
              ${getHeaderHTML(customSectionName.toUpperCase())}
              <ul style="list-style-type: disc; padding-left: 20px; margin: 0;">
                ${items.map(item => `
                  <li style="color: ${textColor}; ${getListStyle()}">${item}</li>
                `).join('')}
              </ul>
            </div>
          `;
        }
        return '';
    }
  };

  // Main pagination function
  const paginateContent = useCallback(() => {
    // Build complete HTML for measurement
    const headerHTML = getHeaderHTML();
    const mainSectionsHTML = rightSectionOrder.map(sectionId => renderSectionHTML(sectionId, false)).filter(html => html !== '');
    const sidebarSectionsHTML = leftSectionOrder.map(sectionId => renderSectionHTML(sectionId, true)).filter(html => html !== '');

    // Create temporary container for measurement
    const measureContainer = document.createElement('div');
    measureContainer.style.position = 'absolute';
    measureContainer.style.visibility = 'hidden';
    measureContainer.style.width = `${pageWidthPx}px`;
    measureContainer.style.fontFamily = font;
    measureContainer.style.fontSize = getFontSizeWithMultiplier();
    measureContainer.style.fontStyle = getFontStyle();
    measureContainer.style.lineHeight = getLineHeight();
    document.body.appendChild(measureContainer);

    // Measure header height
    measureContainer.innerHTML = headerHTML;
    const headerElement = measureContainer.firstElementChild;
    const headerHeight = headerElement ? headerElement.offsetHeight : 0;

    // Calculate available content height per page (excluding top/bottom margins and header on first page)
    const heightBuffer = 10;
    const availableHeightPerPage = pageHeightPx - (2 * topBottomMargin) - heightBuffer;
    const availableMainHeightFirstPage = pageHeightPx - headerHeight - topBottomMargin - heightBuffer;
    const availableSidebarHeightFirstPage = pageHeightPx - headerHeight - (2 * topBottomMargin) - heightBuffer;
    const availableMainHeightOtherPages = pageHeightPx - (2 * topBottomMargin) - heightBuffer;

    // Measure and paginate main sections
    const mainSectionsWithHeights = [];
    for (const sectionHTML of mainSectionsHTML) {
      measureContainer.innerHTML = sectionHTML;
      const sectionElement = measureContainer.firstElementChild;
      if (sectionElement) {
        sectionElement.style.width = `${mainColumnWidthPx}px`;
        const height = sectionElement.offsetHeight;
        mainSectionsWithHeights.push({ html: sectionHTML, height });
      }
    }

    // Measure and paginate sidebar sections
    const sidebarSectionsWithHeights = [];
    for (const sectionHTML of sidebarSectionsHTML) {
      measureContainer.innerHTML = sectionHTML;
      const sectionElement = measureContainer.firstElementChild;
      if (sectionElement) {
        sectionElement.style.width = `${sidebarColumnWidthPx}px`;
        const height = sectionElement.offsetHeight;
        sidebarSectionsWithHeights.push({ html: sectionHTML, height });
      }
    }

    // Clean up
    document.body.removeChild(measureContainer);

    // Paginate main sections
    const mainPages = [];
    let currentMainPage = [];
    let currentMainHeight = 0;
    let isFirstPage = true;

    for (const section of mainSectionsWithHeights) {
      const availableHeight = isFirstPage ? availableMainHeightFirstPage : availableMainHeightOtherPages;
      
      if (currentMainHeight + section.height > availableHeight && currentMainPage.length > 0) {
        mainPages.push([...currentMainPage]);
        currentMainPage = [section.html];
        currentMainHeight = section.height;
        isFirstPage = false;
      } else {
        currentMainPage.push(section.html);
        currentMainHeight += section.height;
      }
    }
    if (currentMainPage.length > 0) {
      mainPages.push([...currentMainPage]);
    }

    // Paginate sidebar sections to match number of main pages
    const sidebarPages = [];
    if (sidebarSectionsWithHeights.length > 0) {
      let currentSidebarPage = [];
      let currentSidebarHeight = 0;
      let sidebarFirstPage = true;

      for (const section of sidebarSectionsWithHeights) {
        const availableHeight = sidebarFirstPage ? availableSidebarHeightFirstPage : availableMainHeightOtherPages;
        
        if (currentSidebarHeight + section.height > availableHeight && currentSidebarPage.length > 0) {
          sidebarPages.push([...currentSidebarPage]);
          currentSidebarPage = [section.html];
          currentSidebarHeight = section.height;
          sidebarFirstPage = false;
        } else {
          currentSidebarPage.push(section.html);
          currentSidebarHeight += section.height;
        }
      }
      if (currentSidebarPage.length > 0) {
        sidebarPages.push([...currentSidebarPage]);
      }
    }

    // Ensure sidebar pages match main pages count
    while (sidebarPages.length < mainPages.length) {
      sidebarPages.push([]);
    }
    while (sidebarPages.length > mainPages.length) {
      sidebarPages.pop();
    }

    // Create final pages
    const newPages = mainPages.map((main, index) => ({
      main: main,
      sidebar: sidebarPages[index] || []
    }));

    setPages(newPages);
    setCurrentPage(0);
  }, [pageWidthPx, pageHeightPx, topBottomMargin, sideMargins, rightSectionOrder, leftSectionOrder, educationEntries, savedSkills, savedSummary, workExperiences, savedAdditionalInfo, savedLanguages, savedAccomplishments, savedCertifications, savedReferences, softwareSkills, savedVolunteering, savedInterests, savedWebsites, customSections, font, fontSize, lineSpacing, headingSize, sectionSpacing, paragraphSpacing, paragraphIndent]);

  // Render header section for display
  const renderHeaderSection = () => (
    <Box className="header-section-measure" sx={{ p: `${topBottomMargin}px ${sideMargins}px ${topBottomMargin / 2}px ${sideMargins}px`, ...textStyles }}>
      <Grid container alignItems="center">
        <Grid item xs={8} sx={{ width: "70%" }}>
          <HeaderContainer>
            <Box sx={{ p: 1.5, mr: 2, backgroundColor: palette.accentColor, borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PersonIcon sx={{ color: '#fff', fontSize: getFontSizeWithMultiplier(2.5) }} />
            </Box>
            <Box>
              <Box sx={{ display: "flex" }}>
                <NameTitle
                  color={palette.nameColor}
                  font={font}
                  fontSize={getFontSizeWithMultiplier(2.5)}
                  fontStyle={getFontStyle()}
                  lineHeight={getLineHeight()}
                  variant="h4"
                >
                  {data.firstName}
                </NameTitle>
                <NameTitle
                  color={palette.nameColor}
                  font={font}
                  fontSize={getFontSizeWithMultiplier(2.5)}
                  fontStyle={getFontStyle()}
                  lineHeight={getLineHeight()}
                  variant="h4"
                  sx={{ mt: -0.5, ml: 1 }}
                >
                  {data.lastName}
                </NameTitle>
              </Box>
              <Typography
                variant="subtitle1"
                sx={{
                  color: palette.lightText,
                  mt: 0.5,
                  fontFamily: font,
                  fontWeight: 500,
                  letterSpacing: '1px',
                  fontSize: getFontSizeWithMultiplier(1.1),
                  fontStyle: getFontStyle(),
                  lineHeight: getLineHeight(),
                }}
              >
                {data.currentPosition || data.role}
              </Typography>
            </Box>
          </HeaderContainer>
        </Grid>
        <Grid item xs={4} sx={{ textAlign: 'right', width: "30%" }}>
          <ContactInfoItem
            color={palette.accentColor}
            font={font}
            fontSize={getFontSizeWithMultiplier()}
            fontStyle={getFontStyle()}
            lineHeight={getLineHeight()}
          >
            <EmailIcon />
            <Typography variant="body2" sx={{ color: palette.textColor, ...textStyles }}>
              {data.email}
            </Typography>
          </ContactInfoItem>
          <ContactInfoItem
            color={palette.accentColor}
            font={font}
            fontSize={getFontSizeWithMultiplier()}
            fontStyle={getFontStyle()}
            lineHeight={getLineHeight()}
          >
            <PhoneIcon />
            <Typography variant="body2" sx={{ color: palette.textColor, ...textStyles }}>
              {data.phone}
            </Typography>
          </ContactInfoItem>
          <ContactInfoItem
            color={palette.accentColor}
            font={font}
            fontSize={getFontSizeWithMultiplier()}
            fontStyle={getFontStyle()}
            lineHeight={getLineHeight()}
          >
            <LocationOnIcon />
            <Typography variant="body2" sx={{ color: palette.textColor, ...textStyles }}>
              {data.city}
            </Typography>
          </ContactInfoItem>
          {data.linkedIn && (
            <ContactInfoItem
              color={palette.accentColor}
              font={font}
              fontSize={getFontSizeWithMultiplier()}
              fontStyle={getFontStyle()}
              lineHeight={getLineHeight()}
            >
              <LinkedInIcon />
              <Typography variant="body2" sx={{ color: palette.textColor, ...textStyles }}>
                {data.linkedIn}
              </Typography>
            </ContactInfoItem>
          )}
          {data.website && (
            <ContactInfoItem
              color={palette.accentColor}
              font={font}
              fontSize={getFontSizeWithMultiplier()}
              fontStyle={getFontStyle()}
              lineHeight={getLineHeight()}
            >
              <LanguageIcon />
              <Typography variant="body2" sx={{ color: palette.textColor, ...textStyles }}>
                {data.website}
              </Typography>
            </ContactInfoItem>
          )}
        </Grid>
      </Grid>
    </Box>
  );

  // Render a single page
  const renderPage = (pageIndex) => {
    if (pages.length === 0) return null;

    const pageData = pages[pageIndex] || { main: [], sidebar: [] };
    const isFirstPage = pageIndex === 0;
    const hasSidebarContent = pageData.sidebar && pageData.sidebar.length > 0;

    return (
      <Box
        key={pageIndex}
        sx={{
          width: page.width,
          height: page.height,
          maxHeight: page.height,
          mx: "auto",
          boxShadow: 4,
          bgcolor: palette.header,
          ...textStyles,
          my: topBottomMargin / 10,
          border: `${lineWeight}px solid ${palette.accentColor}`,
          overflow: 'hidden',
          position: 'relative',
          display: exporting ? 'flex' : (pageIndex === currentPage ? 'flex' : 'none'),
          flexDirection: 'column',
          pageBreakAfter: exporting && pageIndex < pages.length - 1 ? 'always' : 'auto',
          '@media print': {
            boxShadow: 'none',
            my: 0,
            border: 'none',
            pageBreakAfter: pageIndex < pages.length - 1 ? 'always' : 'auto'
          }
        }}
      >
        {isFirstPage && renderHeaderSection()}
        <Grid container sx={{ flex: 1, p: 0 }}>
          {/* Main Content Area */}
          <Grid item xs={8.4} sx={{ p: isFirstPage ? `0 ${sideMargins}px ${topBottomMargin}px ${sideMargins}px` : `${topBottomMargin}px ${sideMargins}px`, pt: 0, width: "70%", maxWidth: "70%", flexBasis: "70%" }}>
            <div style={{ wordBreak: 'break-word' }}>
              {pageData.main.map((html, idx) => (
                <div key={idx} dangerouslySetInnerHTML={{ __html: html }} />
              ))}
              {!isFirstPage && pageData.main.length === 0 && (
                <Typography sx={{ textAlign: 'center', color: '#999' }}>No content</Typography>
              )}
            </div>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={3.6} sx={{ width: "30%", maxWidth: "30%", flexBasis: "30%" }}>
            <Box sx={{
              backgroundColor: palette.accentColor,
              p: `${topBottomMargin}px ${sideMargins}px`,
              color: palette.sidebarText,
              height: '100%',
              minHeight: isFirstPage ? 'calc(100% - 100px)' : '100%',
              ...textStyles
            }}>
              {pageData.sidebar.map((html, idx) => (
                <div key={idx} dangerouslySetInnerHTML={{ __html: html }} />
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Regular renderSectionContent for display (keeping original for compatibility)
  const renderSectionContent = (sectionId, inSidebar = false, isForMeasurement = false) => {
    const textColor = inSidebar ? palette.sidebarText : palette.textColor;

    const SectionHeader = ({ children, ...props }) =>
      inSidebar ? (
        <SidebarSectionHeader
          headerColor={palette.sidebarText}
          font={font}
          fontSize={getHeadingSize()}
          fontStyle={getFontStyle()}
          lineHeight={getLineHeight()}
          {...props}
        >
          {children}
        </SidebarSectionHeader>
      ) : (
        <MainSectionHeader
          headerColor={palette.accentColor}
          font={font}
          fontSize={getHeadingSize()}
          fontStyle={getFontStyle()}
          lineHeight={getLineHeight()}
          {...props}
        >
          {children}
        </MainSectionHeader>
      );

    const getTextStyles = () => {
      if (isForMeasurement) {
        return {
          ...textStyles,
          visibility: 'visible',
          position: 'relative',
        };
      }
      return textStyles;
    };

    switch (sectionId) {
      case 'profile':
        return (
          <Box key="profile" className="resume-section" sx={{ mb: sectionSpacing / 10, ...getTextStyles() }}>
            <SectionHeader>PROFILE</SectionHeader>
            <Typography variant="body2" sx={{ color: textColor, ...getTextStyles() }}>
              {savedSummary || data.profile}
            </Typography>
          </Box>
        );

      case 'workExperience':
        return (
          <Box key="workExperience" className="resume-section" sx={{ mb: sectionSpacing / 10, ...getTextStyles() }}>
            <SectionHeader>WORK EXPERIENCE</SectionHeader>
            {workExperiences && workExperiences.length > 0 ? (
              workExperiences.map((work, i) => (
                <Box key={i} sx={{ mb: paragraphSpacing / 10, ...getTextStyles() }}>
                  <Typography variant="h6" sx={{ ...subheadingStyles, color: textColor }}>
                    {work.jobTitle}
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: getFontStyle(), color: inSidebar ? 'rgba(255,255,255,0.8)' : palette.lightText, ...getTextStyles() }}>
                    {work.employer} ({formatDate(work.startMonth, work.startYear)} - {work.current ? "Present" : formatDate(work.endMonth, work.endYear)})
                  </Typography>
                  {work.description && (
                    <Typography variant="body2" sx={{ color: textColor, mt: paragraphSpacing / 10, ...getTextStyles() }}>
                      {work.description}
                    </Typography>
                  )}
                </Box>
              ))
            ) : (
              <Box sx={{ mb: paragraphSpacing / 10, ...getTextStyles() }}>
                <Typography variant="h6" sx={{ ...subheadingStyles, color: textColor }}>GRAPHIC DESIGNER</Typography>
                <Typography variant="body2" sx={{ fontStyle: getFontStyle(), color: inSidebar ? 'rgba(255,255,255,0.8)' : palette.lightText, ...getTextStyles() }}>
                  Lorem Ipsum Digital Agency (2017-2019)
                </Typography>
                <Typography variant="body2" sx={{ color: textColor, mt: paragraphSpacing / 10, ...getTextStyles() }}>
                  Curabitur sit amet quam ut tortor pellentesque semper at dapibus metus.
                </Typography>
              </Box>
            )}
          </Box>
        );

      case 'education':
        return educationEntries.length > 0 ? (
          <Box key="education" className="resume-section" sx={{ mb: sectionSpacing / 10, ...getTextStyles() }}>
            <SectionHeader>EDUCATION</SectionHeader>
            {educationEntries.map((edu, index) => {
              if (edu.educationLevel === "school") {
                return (
                  <Box key={edu.id} sx={{ mb: index === educationEntries.length - 1 ? 0 : paragraphSpacing / 10, ...getTextStyles() }}>
                    <Typography variant="body2" sx={{ ...subheadingStyles, color: textColor }}>
                      {`${edu.standard} Standard`} {edu.stream ? `- ${edu.stream}` : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: getFontStyle(), color: inSidebar ? 'rgba(255,255,255,0.8)' : palette.lightText, ...getTextStyles() }}>
                      {edu.schoolName} {edu.schoolLocation ? `(${edu.schoolLocation})` : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: getFontStyle(), color: inSidebar ? 'rgba(255,255,255,0.8)' : palette.lightText, ...getTextStyles() }}>
                      {edu.percentage ? `Percentage: ${edu.percentage}%` : ''}
                    </Typography>
                  </Box>
                );
              } else if (edu.educationLevel === "college") {
                return (
                  <Box key={edu.id} sx={{ mb: index === educationEntries.length - 1 ? 0 : paragraphSpacing / 10, ...getTextStyles() }}>
                    <Typography variant="body2" sx={{ ...subheadingStyles, color: textColor }}>
                      {edu.degreeName} {edu.specialization ? `- ${edu.specialization}` : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: getFontStyle(), color: inSidebar ? 'rgba(255,255,255,0.8)' : palette.lightText, ...getTextStyles() }}>
                      {edu.collegeName} {edu.collegeLocation ? `(${edu.collegeLocation})` : ''} ({formatDate(edu.gradMonth, edu.gradYear)})
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: getFontStyle(), color: inSidebar ? 'rgba(255,255,255,0.8)' : palette.lightText, ...getTextStyles() }}>
                      {edu.cgpa ? (edu.programType ? `CGPA: ${edu.cgpa}` : `CGPA: ${edu.cgpa}`) : ''}
                      {edu.grade ? (edu.cgpa ? ` | Grade: ${edu.grade}` : `Grade: ${edu.grade}`) : ''}
                    </Typography>
                    {edu.additionalCoursework && (
                      <Typography variant="body2" sx={{ fontStyle: getFontStyle(), color: inSidebar ? 'rgba(255,255,255,0.8)' : palette.lightText, ...getTextStyles() }}>
                        {edu.additionalCoursework}
                      </Typography>
                    )}
                  </Box>
                );
              }
              return null;
            })}
          </Box>
        ) : null;

      case 'skills':
        return (
          <Box key="skills" className="resume-section" sx={{ mb: sectionSpacing / 10, ...getTextStyles() }}>
            <SectionHeader>SKILLS</SectionHeader>
            <List sx={{ listStyleType: 'disc', pl: 2 }}>
              {savedSkills.length > 0 ? (
                savedSkills.map((skill, i) => (
                  <ListItem key={i} sx={{ display: 'list-item', p: 0, pl: 1, mb: paragraphSpacing / 20 }}>
                    <Typography sx={{ color: textColor, ...getTextStyles() }}>
                      {skill.name} {skill.rating ? `(${skill.rating}%)` : ''}
                    </Typography>
                  </ListItem>
                ))
              ) : (
                <>
                  <ListItem sx={{ display: 'list-item', p: 0, pl: 1, mb: paragraphSpacing / 20 }}>
                    <Typography sx={{ color: textColor, ...getTextStyles() }}>Graphic Design</Typography>
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', p: 0, pl: 1, mb: paragraphSpacing / 20 }}>
                    <Typography sx={{ color: textColor, ...getTextStyles() }}>Leadership</Typography>
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', p: 0, pl: 1, mb: paragraphSpacing / 20 }}>
                    <Typography sx={{ color: textColor, ...getTextStyles() }}>Web Development</Typography>
                  </ListItem>
                </>
              )}
            </List>
          </Box>
        );

      case 'languages':
        return savedLanguages.length > 0 ? (
          <Box key="languages" className="resume-section" sx={{ mb: sectionSpacing / 10, ...getTextStyles() }}>
            <SectionHeader>LANGUAGES</SectionHeader>
            <List sx={{ listStyleType: 'disc', pl: 2 }}>
              {savedLanguages.map((lang, i) => (
                <ListItem key={i} sx={{ display: 'list-item', p: 0, pl: 1, mb: paragraphSpacing / 20 }}>
                  <Typography sx={{ color: textColor, ...getTextStyles() }}>
                    {lang.name}{lang.level ? ` - ${lang.level}` : ''}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        ) : null;

      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <Box key="accomplishments" className="resume-section" sx={{ mb: sectionSpacing / 10, ...getTextStyles() }}>
            <SectionHeader>ACCOMPLISHMENTS</SectionHeader>
            <List sx={{ listStyleType: 'disc', pl: 2 }}>
              {savedAccomplishments.map((acc, i) => (
                <ListItem key={i} sx={{ display: 'list-item', p: 0, pl: 1, mb: paragraphSpacing / 20 }}>
                  <Typography sx={{ color: textColor, ...getTextStyles() }}>{acc}</Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        ) : null;

      case 'certifications':
        return savedCertifications.length > 0 ? (
          <Box key="certifications" className="resume-section" sx={{ mb: sectionSpacing / 10, ...getTextStyles() }}>
            <SectionHeader>CERTIFICATIONS</SectionHeader>
            <List sx={{ listStyleType: 'disc', pl: 2 }}>
              {savedCertifications.map((cert, i) => (
                <ListItem key={i} sx={{ display: 'list-item', p: 0, pl: 1, mb: paragraphSpacing / 20 }}>
                  <Typography sx={{ color: textColor, ...getTextStyles() }}>
                    {cert.name} - {cert.provider} ({cert.year})
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        ) : null;

      case 'softwareSkills':
        return softwareSkills.length > 0 ? (
          <Box key="softwareSkills" className="resume-section" sx={{ mb: sectionSpacing / 10, ...getTextStyles() }}>
            <SectionHeader>SOFTWARE SKILLS</SectionHeader>
            <List sx={{ listStyleType: 'disc', pl: 2 }}>
              {softwareSkills.map((skill, i) => (
                <ListItem key={i} sx={{ display: 'list-item', p: 0, pl: 1, mb: paragraphSpacing / 20 }}>
                  <Typography sx={{ color: textColor, ...getTextStyles() }}>
                    {skill.name} - {skill.level}%
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        ) : null;

      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <Box key="volunteering" className="resume-section" sx={{ mb: sectionSpacing / 10, ...getTextStyles() }}>
            <SectionHeader>VOLUNTEERING</SectionHeader>
            {savedVolunteering.map((vol, index) => (
              <Box key={index} sx={{ mb: paragraphSpacing / 10, ...getTextStyles() }}>
                <Typography variant="h6" sx={{ ...subheadingStyles, color: textColor }}>
                  {vol.subtopic}
                </Typography>
                <Typography variant="body2" sx={{ fontStyle: getFontStyle(), color: inSidebar ? 'rgba(255,255,255,0.8)' : palette.lightText, ...getTextStyles() }}>
                  {vol.fromDate} - {vol.toDate}
                </Typography>
                <Typography variant="body2" sx={{ color: textColor, mt: paragraphSpacing / 20, ...getTextStyles() }}>
                  {vol.content}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : null;

      case 'interests':
        return savedInterests.length > 0 ? (
          <Box key="interests" className="resume-section" sx={{ mb: sectionSpacing / 10, ...getTextStyles() }}>
            <SectionHeader>INTERESTS</SectionHeader>
            <List sx={{ listStyleType: 'disc', pl: 2 }}>
              {savedInterests.map((interest, i) => (
                <ListItem key={i} sx={{ display: 'list-item', p: 0, pl: 1, mb: paragraphSpacing / 20 }}>
                  <Typography sx={{ color: textColor, ...getTextStyles() }}>{interest}</Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        ) : null;

      case 'references':
        return savedReferences.length > 0 ? (
          <Box key="references" className="resume-section" sx={{ mb: sectionSpacing / 10, ...getTextStyles() }}>
            <SectionHeader>REFERENCES</SectionHeader>
            <List sx={{ listStyleType: 'disc', pl: 2 }}>
              {savedReferences.map((ref, i) => (
                <ListItem key={i} sx={{ display: 'list-item', p: 0, pl: 1, mb: paragraphSpacing / 20 }}>
                  <Typography sx={{ color: textColor, ...getTextStyles() }}>{ref}</Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        ) : null;

      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <Box key="additionalInfo" className="resume-section" sx={{ mb: sectionSpacing / 10, ...getTextStyles() }}>
            <SectionHeader>ADDITIONAL INFORMATION</SectionHeader>
            <List sx={{ listStyleType: 'disc', pl: 2 }}>
              {savedAdditionalInfo.map((info, i) => (
                <ListItem key={i} sx={{ display: 'list-item', p: 0, pl: 1, mb: paragraphSpacing / 20 }}>
                  <Typography sx={{ color: textColor, ...getTextStyles() }}>{info}</Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        ) : null;

      case 'websites':
        return savedWebsites.length > 0 ? (
          <Box key="websites" className="resume-section" sx={{ mb: sectionSpacing / 10, ...getTextStyles() }}>
            <SectionHeader>WEBSITES & PROFILES</SectionHeader>
            <List sx={{ listStyleType: 'disc', pl: 2 }}>
              {savedWebsites.map((item, i) => {
                const label = typeof item === 'string' ? item : (item.label || item.name || item.title || '');
                const url = typeof item === 'object' ? (item.url || item.link || '') : '';
                return (
                  <ListItem key={i} sx={{ display: 'list-item', p: 0, pl: 1, mb: paragraphSpacing / 20 }}>
                    <Typography sx={{ color: textColor, ...getTextStyles() }}>
                      {url && label !== url ? `${label}: ${url}` : (label || url)}
                    </Typography>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ) : null;

      default:
        if (sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName] || [];
          if (items && items.length > 0) {
            return (
              <Box key={sectionId} className="resume-section" sx={{ mb: sectionSpacing / 10, ...getTextStyles() }}>
                <SectionHeader>{customSectionName.toUpperCase()}</SectionHeader>
                <List sx={{ listStyleType: 'disc', pl: 2 }}>
                  {items.map((item, i) => (
                    <ListItem key={i} sx={{ display: 'list-item', p: 0, pl: 1, mb: paragraphSpacing / 20 }}>
                      <Typography sx={{ color: textColor, ...getTextStyles() }}>{item}</Typography>
                    </ListItem>
                  ))}
                </List>
              </Box>
            );
          }
          return null;
        }
        return null;
    }
  };

  // Render sidebar sections based on current order
  const renderSidebarSections = (pageIndex) => {
    return leftSectionOrder.map(sectionId => renderSectionContent(sectionId, true)).filter(Boolean);
  };

  // Render drag panel for arrange sections dialog
  const renderDragPanel = (side, sections) => {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <DragIndicator sx={{ mr: 1 }} />
          {side === 'left' ? 'Left Sidebar' : 'Right Main Content'} Sections
          <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
            ({sections.length} sections)
          </Typography>
        </Typography>
        <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
          {sections.length > 0 ? (
            sections.map((sectionId, index) => (
              <Paper
                key={sectionId}
                draggable={enableDragDrop}
                onDragStart={(e) => side === 'left' ? handleLeftDragStart(e, index) : handleRightDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index, side)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => side === 'left' ? handleLeftDrop(e, index) : handleRightDrop(e, index)}
                onDragEnd={handleDragEnd}
                sx={{
                  p: 2,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: dragOverIndex === index && dragOverSide === side ? '#f0f0f0' : 'white',
                  boxShadow: dragOverIndex === index && dragOverSide === side ? 2 : 1,
                  cursor: enableDragDrop ? 'grab' : 'default',
                  '&:active': { cursor: enableDragDrop ? 'grabbing' : 'default' },
                  transition: 'all 0.2s ease',
                }}
              >
                <DragIndicator color="action" />
                <Typography sx={{ ml: 1, flexGrow: 1 }}>{getSectionTitle(sectionId)}</Typography>
                <Typography variant="body2" color="textSecondary">{index + 1}</Typography>
              </Paper>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
              No sections. Drag sections from the other side.
            </Typography>
          )}
        </Box>
        <Button onClick={side === 'left' ? resetLeftSectionOrder : resetRightSectionOrder} variant="outlined" size="small" sx={{ mt: 1 }}>
          Reset {side === 'left' ? 'Left' : 'Right'} Order
        </Button>
      </Box>
    );
  };

  // Trigger pagination when content changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      paginateContent();
    }, 150);
    return () => clearTimeout(timeoutId);
  }, [paginateContent, educationEntries, savedSkills, savedSummary, workExperiences, savedAdditionalInfo, savedLanguages, savedAccomplishments, savedCertifications, savedReferences, softwareSkills, savedVolunteering, savedInterests, savedWebsites, customSections, rightSectionOrder, leftSectionOrder, topBottomMargin, sideMargins, paragraphSpacing, headingSize, lineSpacing, fontSize, font, fontStyle, paragraphIndent, lineWeight, pageSize]);

  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      {/* Controls - Hidden during print */}
      {!exporting && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 2,
          gap: 2,
          '@media print': { display: 'none' }
        }}>
          <IconButton onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0} color="primary">
            <ChevronLeft />
          </IconButton>
          <Typography sx={{ mx: 2 }}>
            Page {currentPage + 1} of {Math.max(1, pages.length)}
          </Typography>
          <IconButton onClick={() => setCurrentPage(prev => Math.min(pages.length - 1, prev + 1))} disabled={currentPage >= pages.length - 1} color="primary">
            <ChevronRight />
          </IconButton>

          {enableDragDrop && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 2 }}>
              <Button onClick={handleArrangeSectionsClick} variant="contained" startIcon={<Reorder />} color="primary">
                Arrange Sections
              </Button>
              <Button onClick={resetAllSectionOrder} variant="outlined" color="secondary" size="small">
                Reset All Sections
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Resume Content */}
      <Box id="resume-container">
        {pages.length > 0 ? (
          pages.map((_, index) => renderPage(index))
        ) : (
          <Box sx={{ textAlign: 'center', p: 4 }}>Loading resume content...</Box>
        )}
      </Box>

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={handleArrangeDialogClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Reorder sx={{ mr: 1 }} />
            Arrange Resume Sections
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Drag and drop sections to reorder them within each side or move sections between left sidebar and right main content.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3, width: "100%" }}>
            <Box sx={{ flex: 1, width: "50%" }}>{renderDragPanel("left", leftSectionOrder)}</Box>
            <Box sx={{ flex: 1, width: "50%" }}>{renderDragPanel("right", rightSectionOrder)}</Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAllSectionOrder} color="secondary">Reset All</Button>
          <Button onClick={handleArrangeDialogClose} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Add metadata for filtering
Resume20.resumeMeta = {
  hasPhoto: false,
  columns: 2,
  colorOptions: [
    { value: '#333', label: 'Dark Gray' },
    { value: '#3f51b5', label: 'Indigo' },
    { value: '#2196f3', label: 'Blue' },
    { value: '#f44336', label: 'Red' },
    { value: '#4caf50', label: 'Green' },
    { value: '#ff9800', label: 'Orange' },
    { value: '#9c27b0', label: 'Purple' }
  ],
  nameColorOptions: [
    { value: '#333', label: 'Dark Gray' },
    { value: '#000000', label: 'Black' },
    { value: '#1a2b47', label: 'Dark Blue' },
    { value: '#2e7d32', label: 'Green' },
    { value: '#7b1fa2', label: 'Purple' }
  ],
  sidebarBackgroundOptions: [
    { value: '#333', label: 'Dark Gray' },
    { value: '#1a2b47', label: 'Dark Blue' },
    { value: '#2e7d32', label: 'Green' },
    { value: '#7b1fa2', label: 'Purple' },
    { value: '#c2185b', label: 'Pink' },
    { value: '#d32f2f', label: 'Red' }
  ],
  sidebarTextColorOptions: [
    { value: '#fff', label: 'White' },
    { value: '#f5f5f5', label: 'Light Gray' },
    { value: '#e0e0e0', label: 'Very Light Gray' },
    { value: '#bbdefb', label: 'Light Blue' },
    { value: '#c8e6c9', label: 'Light Green' }
  ],
  headerBackgroundOptions: [
    { value: '#fff', label: 'White' },
    { value: '#f8f9fa', label: 'Light Gray' },
    { value: '#e3f2fd', label: 'Very Light Blue' },
    { value: '#f3e5f5', label: 'Very Light Purple' },
    { value: '#e8f5e9', label: 'Very Light Green' }
  ],
  experienceLevel: ["No Experience", "Less than 3 years", "3-5 Years", "5-10 Years", "10+ Years"]
};

export default Resume20;